import * as React from "react";
import { useNavigate } from "react-router-dom";

import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { usePageViewEventTracker } from "@utils/analytics";
import { colors } from "../../styles/constants";
import { insideIframe } from "../../utils/Functions";
import { Button } from "../../components/atoms/Button";
import { Header } from "../../components/atoms/Header";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { SideImageTemplate } from "@templates/SideImageTemplate";
import { getExpressData } from "./express-data";
import { navigateToURL } from "./navigateToURL";

export type ExpressPaymentSubmittedPageProps = {
  serviceProvider: { companyName: string };
};

export const ExpressPaymentSubmittedPage: React.FC<ExpressPaymentSubmittedPageProps> = ({
  serviceProvider,
}) => {
  usePageViewEventTracker("Express form submitted");

  const navigate = useNavigate();

  const [busy, setBusy] = React.useState(false);

  const done = React.useCallback(
    (url: string) => {
      setBusy(true);

      navigateToURL(navigate, url);
    },
    [setBusy, navigate]
  );

  return (
    <ExpressPaymentSubmittedPageView busy={busy} serviceProvider={serviceProvider} done={done} />
  );
};

export type ExpressPaymentSubmittedPageViewProps = {
  busy?: boolean;
  serviceProvider: { companyName: String };
  done: (url: string) => void;
};

export const ExpressPaymentSubmittedPageView: React.FC<ExpressPaymentSubmittedPageViewProps> = ({
  busy,
  serviceProvider,
  done,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const expressData = getExpressData();

  return (
    <SideImageTemplate
      headerType="help"
      busy={busy}
      image={<img src="/images/stock/express-form-submitted.svg" width="100%" alt="Family" />}
    >
      <Stack rowGap={4}>
        {matches ? (
          <>
            <Header level={2} color={colors.accentTeal}>
              Thank you for completing payment
            </Header>
          </>
        ) : (
          <>
            <Typography variant="body1" color={colors.accentTeal}>
              <Stack columnGap={1} direction="row">
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ color: colors.lightTeal }} />
                <Header level={1} color={colors.accentTeal}>
                  Thank you for completing payment
                </Header>
              </Stack>
            </Typography>
          </>
        )}

        <Typography variant="body1">
          Please proceed to your dashboard to add more accounts. The link to your dashboard is also
          available in the email we have just sent you.
        </Typography>

        <FormStack>
          <FormField twoThirdsWidth>
            {!expressData.formUrl ? null : insideIframe ? (
              <Button
                variant="primary"
                size="large"
                disabled={busy}
                onClick={() => done(expressData.formUrl)}
              >
                Go to dashboard
              </Button>
            ) : (
              <Button variant="primary" size="large" disabled={busy} href={expressData.formUrl}>
                Go to dashboard
              </Button>
            )}
          </FormField>
        </FormStack>
      </Stack>
    </SideImageTemplate>
  );
};
